import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DialogLayout from '../../../components/layout/DialogLayout';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import 'dayjs/locale/en-gb';
import { Stack } from '@mui/material';
import { apiSaveSharePriceUpdate } from "../../../utils/api/api";
import { useSnackbar } from "notistack";
import SharePriceForm from './SharePriceForm';
/**
 * SharePriceUpdate Component
 * 
 * A dialog interface for updating share prices for investment schemes.
 * Handles form state, validation, and submission to the API.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.toggleDialog - Function to toggle the dialog state
 * @param {Object} props.scheme - The selected scheme object
 * @param {Object} props.sharePrice - Optional existing share price data for editing
 * @param {Function} props.onUpdateSuccess - Callback function on successful update
 */
const SharePriceUpdate = ({ open, toggleDialog, scheme, sharePrice, onUpdateSuccess }) => {
  const initialUnitValue = sharePrice?.unitValue || 0.00;
  const initialNotes = sharePrice?.remarks || '';
  const initialDate = sharePrice?.date || new Date().toISOString().split('T')[0];
  
  const [formData, setFormData] = useState({
    unitValue: initialUnitValue,
    notes: initialNotes,
    valuationDate: initialDate
  });
  
  const [formErrors, setFormErrors] = useState({
    unitValueError: false
  });
  
  const [actionLoader, setActionLoader] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Reset form when dialog closes or opens with new data
  useEffect(() => {
    if (!open) {
      resetForm();
    } else if (sharePrice) {
      setFormData({
        unitValue: sharePrice.unitValue || 0.00,
        notes: sharePrice.remarks || '',
        valuationDate: sharePrice.date || new Date().toISOString().split('T')[0]
      });
    }
  }, [open, sharePrice]);

  /**
   * Resets the form to its initial state
   */
  const resetForm = () => {
    setFormData({
      unitValue: initialUnitValue,
      notes: initialNotes,
      valuationDate: initialDate
    });
    setFormErrors({
      unitValueError: false
    });
  };

  /**
   * Handles form field changes
   * @param {string} field - The name of the field being updated
   * @param {any} value - The new value for the field
   */
  const handleFieldChange = (field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [field]: value
    }));

    // Clear validation errors when field is modified
    if (field === 'unitValue') {
      setFormErrors(prevErrors => ({
        ...prevErrors,
        unitValueError: false
      }));
    }
  };

  /**
   * Validates the form data
   * @returns {boolean} Whether the form is valid
   */
  const validateForm = () => {
    const { unitValue } = formData;
    let isValid = true;
    if (Number(unitValue) < 0) {
      setFormErrors(prevErrors => ({
        ...prevErrors,
        unitValueError: true
      }));
      isValid = false;
    }
    
    return isValid;
  };

  /**
   * Handles form submission
   */
  const handleSubmit = () => {
    if (!validateForm()) {
      enqueueSnackbar("Please provide a valid unit value", { variant: "warning" });
      return;
    }
    
    setActionLoader(true);
    const requestBody = {
      date: formData.valuationDate,
      unitValue: Number(formData.unitValue),
      remarks: formData.notes,
      oppRid: scheme?.oppRid
    };

    apiSaveSharePriceUpdate(requestBody)
      .then((response) => {
        setActionLoader(false);
        enqueueSnackbar("Share price updated successfully", { variant: "success" });
        toggleDialog();
        if (onUpdateSuccess) {
          onUpdateSuccess(response);
        }
      })
      .catch((error) => {
        setActionLoader(false);
        enqueueSnackbar(error || "Failed to update share price", { variant: "error" });
      });
  };

  /**
   * Handles cancellation of the form
   */
  const handleCancel = () => {
    resetForm();
    toggleDialog();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <DialogLayout
        open={open}
        title="Share Price Update"
        subtitle={scheme?.oppTitle}
        maxWidth="sm"
        actionLoader={actionLoader}
        closeAction={handleCancel}
        negativeAction={{ label: 'Cancel', onClick: handleCancel }}
        positiveAction={{ label: 'Save', onClick: handleSubmit }}
      >
        <SharePriceForm 
          formData={formData}
          formErrors={formErrors}
          onFieldChange={handleFieldChange}
        />
      </DialogLayout>
    </LocalizationProvider>
  );
};

SharePriceUpdate.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDialog: PropTypes.func.isRequired,
  scheme: PropTypes.object,
  sharePrice: PropTypes.object,
  onUpdateSuccess: PropTypes.func
};

export default SharePriceUpdate;